// The narrow entry, never the bare barrel: this module is on the eager startup graph (F1, 2026-10-12).
import { isCapacitorReservedHref } from "@seavien/commons/embedUrlSafety";
import {
    BROWSER_NAVIGATION_EVENT_NAME,
    type BrowserNavigationEventDetail,
    performBrowserNavigation,
} from "@/main/common/BrowserNavigation";
import { remountFrontEndSubtree } from "@/main/bootstrap/bootstrapFrontEnd";
import { fetchHtmlDocument, probeFrontendRelease } from "@/main/navigation/DocumentFetcher";
import type { ClientRouteMatch } from "@/main/navigation/ClientRouteDescriptor";
import { buildClientRouteMountElement, replaceNavigationRootContent } from "@/main/navigation/ClientRouteMountBuilder";
import { applyClientRouteDocumentState } from "@/main/navigation/ClientRouteDocumentState";
import { runPopStateGuards } from "@/main/navigation/PopStateGuardRegistry";
import { runNavigationDepartureObservers, runNavigationLeaveGuards } from "@/main/navigation/NavigationLeaveGuardRegistry";
import { resolveClientRoute } from "@/main/navigation/ClientRouteRegistry";
import { VIEWPORT_CONTRACT_ATTRIBUTE } from "@/main/navigation/NavigationRootShell";
import {
    ensureDocumentReleaseMatchesCurrent,
    ensureProbedReleaseMatchesCurrent,
    FrontendReleaseHardReloadRequiredError,
} from "@/main/navigation/FrontendReleaseGuard";
import { updateShellStateSnapshot } from "@/main/navigation/ShellStateStore";
// FI-3157 (r158): never `PageNavigationTransition` itself (lazy now): the classifier is eager and small, and the
// skeleton/held-page calls go through the facade that loads the module (header-startup-cost design §8).
import { hasNavigationTransition, prefetchNavigationTransitionData } from "@/main/navigation/NavigationTransitionRoutes";
import {
    clearNavigationTransitionSkeleton,
    discardHeldNavigationPage,
    restoreHeldNavigationPage,
    schedulePageNavigationTransitionIdlePreload,
    showNavigationTransitionSkeleton,
} from "@/main/navigation/pageNavigationTransitionLazy";
import { applyPostMountDocumentEffectsFromDocument } from "@/main/navigation/NavigationDocumentEffects";
import { recordNavigationConsumedPrefetch } from "@/main/navigation/NavigationPrefetchTelemetry";
import {
    getAnchorNavigationPolicy,
    getIntentNavigationPolicy,
    getPopStateNavigationPolicy,
    type NavigationPolicyDecision,
} from "@/main/navigation/NavigationPolicy";
import { CrossLocaleNavigationUnsupportedError, swapDocumentContent } from "@/main/navigation/DocumentSwap";
import { runNavigationViewTransition } from "@/main/navigation/PageViewTransition";
import { handleOutboundAnchorNavigation } from "@/main/mobile-shell/MobileOutboundLinkBridge";
import { findClosestAnchor, isCurrentPageReselect } from "@/main/navigation/NavigationPolicy";
import { prefersReducedMotion } from "@/main/util/motionPreference";
import {
    completeNavigationProgress,
    failNavigationProgress,
    startNavigationProgress,
} from "@/main/navigation/NavigationProgress";
import { flushUiInteractionCensusOnNavigation } from "@/main/analytics/uiInteractionCensus";
import { beginNavigation } from "@/main/navigation/NavigationState";
import type {
    EnhancedNavigationRequest,
} from "@/main/navigation/NavigationTypes";
import {
    isEnhancedNavigationHistoryState,
    notifyInPageHistoryChange,
    pushPageHistoryEntry,
    readBootNavigationType,
    replaceCurrentHistoryStateScroll,
    replacePageHistoryEntry,
    restoreScrollPosition,
    restoreBootFragmentScroll,
    restoreSoftNavigationFragmentScroll,
    seedBootHistoryStateScroll,
} from "@/main/navigation/NavigationHistoryState";
import { moveFocusToNavigatedPage } from "@/main/navigation/NavigationFocus";
import {
    hasUnresolvedPathSegment,
    reportUnresolvedUrlRefusal,
} from "@/main/navigation/UnresolvedPathSegment";
import { beginPageInstance, getPageInstanceId, retirePageInstance } from "@/main/navigation/CurrentPageInstance";
import {
    installPopDispatchTelemetry,
    recordPopDispatch,
    resetPopDispatchTelemetryForTests,
} from "@/main/navigation/PopDispatchTelemetry";
import logger from "@/lib/logger";
import { unmountReactTreeWithin } from "@/main/react/mounting";
import { clearNavigationLoadFailure, showNavigationLoadFailure } from "@/main/navigation/NavigationLoadFailure";
import {
    placeNavigationSkeletonCarry,
    removeNavigationSkeletonCarry,
    takeNavigationSkeletonForCarry,
} from "@/main/navigation/NavigationSkeletonCarry";

let enhancedNavigationSetupComplete = false;
let activeNavigationAbortController: AbortController | null = null;
let activeNavigationSequence = 0;
/** Set by `performHardFallback`; read by `handlePageHide` (FI-2812). */
let hardFallbackPending = false;

/** A hard load chosen by enhanced navigation itself (a failed soft navigation, or a pop that must go hard). */
function performHardFallback(href: string, mode: Parameters<typeof performBrowserNavigation>[1]): void {
    hardFallbackPending = true;
    performBrowserNavigation(href, mode);
}

/**
 * FI-2814 (work order round 116 D15): a soft navigation that has not settled by this deadline is abandoned into the
 * inline retry block (`NavigationLoadFailure`). Design: enhanced-navigation design §11.4.
 */
export const NAVIGATION_SETTLE_TIMEOUT_MS = 15_000;
let navigationSettleTimeoutMs = NAVIGATION_SETTLE_TIMEOUT_MS;

/** Test seam: a spec shortens the deadline instead of driving fifteen seconds of fake timers through the awaits. */
export function setNavigationSettleTimeoutForTests(timeoutMs?: number): void {
    navigationSettleTimeoutMs = timeoutMs ?? NAVIGATION_SETTLE_TIMEOUT_MS;
}

type NavigationLoadFailureReason = "timeout" | "offline" | "network";

const NETWORK_FAILURE_CODES = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);

/**
 * A failure the network caused, as opposed to one the server or the swap caused: the browser says it is offline, or
 * axios rejected with no response and a transport code (connection refused/dropped, the shared instance's timeout).
 * Only these end in the inline block; any other failure keeps the full-load fallback, which is the better recovery
 * when the network works.
 */
function classifyNetworkFailure(error: unknown): NavigationLoadFailureReason | null {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return "offline";
    }
    const candidate = error as { code?: unknown; response?: unknown } | null | undefined;
    if (candidate && typeof candidate.code === "string" && NETWORK_FAILURE_CODES.has(candidate.code)
        && candidate.response == null) {
        return "network";
    }
    return null;
}

function ensureInitialHistoryState(): void {
    // The boot document is the first page instance. Minting here (rather than relying on the module's
    // initial value) also publishes `window.__seavienPageInstanceId` before any island mounts.
    beginPageInstance();

    if (isEnhancedNavigationHistoryState(window.history.state)) {
        // FI-2812: a cross-document Back/Forward keeps (and restores) the offset the entry was left at.
        if (seedBootHistoryStateScroll()) {
            return;
        }
    } else {
        replacePageHistoryEntry(window.location.href, window.scrollX, window.scrollY);
    }

    // FI-4063: a hard landing on a fragment ends on it once the islands have rendered over the SSR markup. Not on a
    // Back/Forward boot: the member left that entry where they chose (a saved 0,0 included), not at its fragment.
    if (readBootNavigationType() !== "back_forward") {
        restoreBootFragmentScroll();
    }
}

/**
 * FI-2812: a document left by a HARD navigation (a non-SAFE route, an external link, a full reload) stamps its live
 * offset onto its entry, so a Back across documents has an offset to restore. Skipped while a soft navigation is in
 * flight: that navigation stamped the outgoing entry when it began, and its skeleton may have scrolled to the top
 * since, so a stamp here would overwrite the real offset with 0 if the navigation then falls back to a hard load.
 * The abort controller alone cannot say so: a failed soft navigation's `finally` clears it before the fallback's
 * `pagehide` fires, so every hard fallback goes through `performHardFallback`, which raises its own flag (r117 r3
 * landing review). A pop that goes hard is skipped too: its destination entry is already current.
 */
function handlePageHide(): void {
    if (activeNavigationAbortController !== null || hardFallbackPending) {
        return;
    }
    try {
        replaceCurrentHistoryStateScroll();
    } catch {
        // A history write refused at unload must not break the unload.
    }
}

/**
 * FI-1312 (mechanism provisional): a signed-in native push tap soft-navigates, so for a client-owned route the release
 * probe is the ONLY request the server sees, and `RefLoggingFilter` stamps `push_delivery_log.opened_at` (`pl`) and
 * `notification_clicks` (`ref=nt-…`, with `nsrc`) from it. A descriptor whose canonical url drops the query (`/groups`,
 * `/user/*`, home, …) sent the probe without them. Carry those three from the requested address onto the probe url;
 * the mounted address stays canonical. A hard-load FALLBACK after an answered probe loads the address WITHOUT them
 * (FI-1896, `withoutTapAttributionParams`), so the tap is recorded once.
 */
const TAP_ATTRIBUTION_PARAMS = ["pl", "ref", "nsrc"];

/** FI-1896: `url` minus the three tap-attribution params; the same instance when it carries none. */
export function withoutTapAttributionParams(url: URL): URL {
    if (!TAP_ATTRIBUTION_PARAMS.some((name) => url.searchParams.has(name))) {
        return url;
    }
    const stripped = new URL(url.href);
    for (const name of TAP_ATTRIBUTION_PARAMS) {
        stripped.searchParams.delete(name);
    }
    return stripped;
}

export function withTapAttributionParams(probeUrl: URL, requestedUrl: URL): URL {
    let augmented: URL | null = null;
    for (const name of TAP_ATTRIBUTION_PARAMS) {
        const value = requestedUrl.searchParams.get(name);
        if (value !== null && !probeUrl.searchParams.has(name)) {
            augmented = augmented ?? new URL(probeUrl.href);
            augmented.searchParams.set(name, value);
        }
    }
    return augmented ?? probeUrl;
}

/**
 * FI-4226 (r164-pw): the address a push/replace on the document path commits. The fetch's response URL never carries a
 * fragment (an XHR `responseURL` drops it), so writing it as-is turned an in-app click on `/discussion/<slug>#section-…`
 * into `/discussion/<slug>` and every island reader of `location.hash` (`useHeadingFragmentArrival`) saw nothing. The
 * REQUESTED fragment is kept when the response has none, as a browser keeps it across a redirect. Not used on a pop.
 */
export function withRequestedFragment(committedUrl: URL, requestedUrl: URL): string {
    if (!requestedUrl.hash || committedUrl.hash) {
        return committedUrl.href;
    }
    const withFragment = new URL(committedUrl.href);
    withFragment.hash = requestedUrl.hash;
    return withFragment.href;
}

function mountClientOwnedRoute(
    routeMatch: ClientRouteMatch,
    request: EnhancedNavigationRequest,
    navigationSequence: number,
): URL {
    const currentRoot = document.querySelector('[data-navigation-root="main-content"]') as HTMLElement | null;
    if (!currentRoot) {
        throw new Error("Enhanced navigation could not find the current navigation root for a client-owned route.");
    }

    // FI-3330: a page a skeleton HELD (rather than unmounted) is unmounted first, before the retire and the history
    // write, exactly where the skeleton used to unmount it: its flushed passive effects still run against the page's
    // own entry and instance (a list banks under its own `navigationId`).
    discardHeldNavigationPage();
    // FI-4824: THIS navigation's skeleton leaves the root before the rebuild below and comes back beside the page's
    // mount container, until the island commits (design §8.3). Null when the root holds no skeleton of this sequence.
    const skeletonCarry = takeNavigationSkeletonForCarry(currentRoot, navigationSequence);
    // BEFORE the history write: the unmount below flushes the outgoing tree's pending passive effects, and from
    // the write until `remountFrontEndSubtree` mints, they must not own the address (ownership design §14).
    retirePageInstance();
    if (request.historyMode === "push") {
        pushPageHistoryEntry(routeMatch.url.href);
    } else if (request.historyMode === "replace") {
        replacePageHistoryEntry(routeMatch.url.href);
    } else {
        // Pop: this entry already exists and the user is returning to it, so it keeps the id it was
        // popped with. Without that, the rewrite here mints a fresh id microseconds before the island
        // mounts, and every mount-time read of per-entry memory (NavigationListStateCache) misses.
        replacePageHistoryEntry(
            routeMatch.url.href,
            request.restoreScrollX ?? 0,
            request.restoreScrollY ?? 0,
            request.restoreNavigationId,
        );
    }

    unmountReactTreeWithin(currentRoot);

    // The shell, before the children. A client route has no incoming document to copy attributes from
    // — `DocumentSwap.syncElementAttributes` is the other path's version of this line — so `<main>`
    // otherwise keeps whatever the LAST FULL LOAD dressed it in. That is how `/messages` spent four
    // days rendering correctly on a hard refresh and broken on every in-app arrival: its whole
    // one-viewport mechanism lives in `messages.jte`'s `mainClass`, and nothing here applied it.
    // Design: 2026-08-11-mobile-chat-viewport-ownership-and-composer-design.md §3d, Stage 8.
    currentRoot.className = routeMatch.navigationRootClassName;
    // The same inheritance, one attribute over (2026-08-16). `data-viewport-contract` scopes the I2
    // probe, so leaving the previous page's value on would either hand `/messages` no contract on
    // every in-app arrival — the probe silently not watching the surface it was built for — or leave
    // `chat` on the page a member navigated away to. Written and REMOVED explicitly, because the
    // absent case is the common one and a stale contract is the direction that raises a false alarm.
    if (routeMatch.viewportContract) {
        currentRoot.setAttribute(VIEWPORT_CONTRACT_ATTRIBUTE, routeMatch.viewportContract);
    } else {
        currentRoot.removeAttribute(VIEWPORT_CONTRACT_ATTRIBUTE);
    }
    // The template's sibling containers first, then the page mount: the same root a server render
    // builds, as SIBLINGS (FI-047; rule 32 forbids nesting one container in another).
    const pageContainer = buildClientRouteMountElement({
        componentName: routeMatch.componentName,
        className: routeMatch.mountClassName,
        dataset: routeMatch.dataset,
    });
    replaceNavigationRootContent(currentRoot, [
        ...(routeMatch.siblingContainers ?? []).map((sibling) => buildClientRouteMountElement({
            componentName: sibling.componentName,
            className: sibling.className,
            dataset: sibling.dataset ?? {},
        })),
        pageContainer,
    ]);
    if (skeletonCarry) {
        // A sibling of the container, never inside it (rule 32), so the island's first commit cannot discard it.
        placeNavigationSkeletonCarry(skeletonCarry, pageContainer);
    }

    if (routeMatch.shellStatePatch) {
        // A client route changes only the search fields, so the header is SPREAD, never rebuilt field by field
        // (FI-1614). A field-by-field rebuild drops every field it forgets to name: `supportUsername` (the support
        // alias read "TheCustodian" after the first in-app navigation) and then `officialUsernames` (SV-166 2b)
        // were each lost that way. The same viewer is signed in before and after a client route, so carrying
        // every field is correct here; the logout writer (`AuthSessionSync`) is the one that must reset fields.
        // The leading defaults only cover a snapshot that never had a header.
        updateShellStateSnapshot((currentSnapshot) => ({
            ...currentSnapshot,
            mainHeader: {
                loggedIn: false,
                profileImageUrl: null,
                username: null,
                searchQuery: "",
                searchType: "discussions",
                ...currentSnapshot.mainHeader,
                ...routeMatch.shellStatePatch,
            },
        }));
    }

    applyClientRouteDocumentState(routeMatch.documentState);
    remountFrontEndSubtree(currentRoot);

    if (request.historyMode === "pop") {
        // The mount above is async (island chunk, then feed data), so the document is still only
        // viewport-tall right now and a one-shot scrollTo would be clamped to 0. Re-apply until the
        // content can hold the offset. NOT awaited on the mount above, deliberately: the restore must be
        // in flight BEFORE the island's first commit, because that commit is what finishes it
        // (`notifyRestoredContentReady`, keyed on this entry's id) — a signal that arrives before the
        // restore starts finds nothing and is dropped. SV-24 slice 4.
        restoreScrollPosition(
            request.restoreScrollX ?? 0,
            request.restoreScrollY ?? 0,
            request.restoreNavigationId,
        );
    }

    return routeMatch.url;
}

async function runEnhancedNavigation(
    request: EnhancedNavigationRequest,
    options: { fromLoadFailureRetry?: boolean } = {},
): Promise<boolean> {
    // FIRST statement in the function, before every side effect — and that position is the fix.
    //
    // The address-bar guard in `NavigationHistoryState` runs via `pushPageHistoryEntry`, which this
    // function does not reach until AFTER `await fetchHtmlDocument(...)` below. So a junk URL used
    // to be fetched from the origin (404, logged in the access log) and only then declined for the
    // address bar. Guarding here means the request is never made.
    //
    // Returning `false` without invoking the hard-navigation fallback is deliberate: falling back
    // would issue the very request this exists to prevent, just through a different seam. Both
    // callers `void` the result, so refusing here simply leaves the user on the current page — which
    // is the same trade the address-bar guard already makes, and strictly better than a 404.
    if (hasUnresolvedPathSegment(request.url.pathname)) {
        reportUnresolvedUrlRefusal(request.url.href, "EnhancedNavigation");
        return false;
    }

    // Drain the UI-interaction census while `currentPageType()` still answers for the page the member
    // is LEAVING. Its docblock has claimed since the module shipped that "the soft-navigation runtime
    // calls this so a batch does not straddle two pages", and `handleClick`'s own comment calls the
    // navigation flush belt-and-braces — and `rg` found zero callers (register FR-R4). Counting was
    // never wrong (every entry is keyed by the page type captured at click time), but a described
    // mechanism that does not exist is worse than an absent one: the next reader reasons about batch
    // boundaries from it. Here rather than beside the swap because there are three commit sites and
    // one entry, and an extra beacon on a navigation that later aborts costs nothing.
    flushUiInteractionCensusOnNavigation();

    // FI-3330: a navigation the member started elsewhere supersedes the Retry banner of an earlier failure; the banner's
    // own Retry keeps it (aria-disabled, focus held) so a second failure re-arms it in place (FI-3413).
    if (!options.fromLoadFailureRetry) {
        clearNavigationLoadFailure();
    }

    const navigationSequence = ++activeNavigationSequence;
    // Soft-nav commit is where a prefetch pays off — mark any prior prefetch of this destination consumed.
    recordNavigationConsumedPrefetch(request.url);
    const endNavigation = beginNavigation();
    const shouldShowNavigationTransitionSkeleton = hasNavigationTransition(request.url);
    const shouldScrollToTopImmediately = shouldShowNavigationTransitionSkeleton && request.trigger === "intent";
    activeNavigationAbortController?.abort();
    const abortController = new AbortController();
    activeNavigationAbortController = abortController;

    // FI-2814: the settle deadline. Aborting our own controller turns a hung probe or fetch into the `AbortError` the
    // catch already handles; the flag is what tells that catch it was the deadline and not a newer tap.
    let settleDeadlinePassed = false;
    const settleDeadlineId = window.setTimeout(() => {
        if (navigationSequence !== activeNavigationSequence) {
            return;
        }
        settleDeadlinePassed = true;
        abortController.abort();
    }, navigationSettleTimeoutMs);
    // Set once a client-owned route is on screen: from then on a stalled release probe must not tear it down.
    let mountedClientRouteUrl: URL | null = null;

    startNavigationProgress();
    // On a pop the current entry is already the *destination*, so saving live scroll here would
    // overwrite the very offset we are about to restore with the pre-render value (~0).
    if (request.historyMode !== "pop") {
        replaceCurrentHistoryStateScroll();
    }
    clearNavigationTransitionSkeleton();
    // FI-4824: a skeleton an earlier client route carried goes with it, whether or not its island ever committed.
    removeNavigationSkeletonCarry();

    // FI-3330: where the member was reading, so a failed navigation can put a held page back at the same offset (the
    // skeleton's scroll-to-top below would otherwise leave the restored page at its top).
    const scrollBeforeNavigation = { x: window.scrollX, y: window.scrollY };
    if (shouldShowNavigationTransitionSkeleton) {
        if (shouldScrollToTopImmediately) {
            window.scrollTo({ left: 0, top: 0, behavior: "auto" });
        }
        showNavigationTransitionSkeleton(request.url, navigationSequence, request.skeletonHints);
        prefetchNavigationTransitionData(request.url);
    }

    // FI-1896: set once the release probe ANSWERED, i.e. the server has seen this navigation's `pl`/`ref`/`nsrc`
    // (`withTapAttributionParams`). Every hard-load fallback below then loads the address without them, so a tap is
    // not recorded twice in `notification_clicks`. A probe that threw before answering leaves them on. FI-1936: the
    // document path's `fetchHtmlDocument` answering sets it too (the name predates that second writer).
    let tapAttributionRecordedByProbe = false;
    const fallbackHref = () => (tapAttributionRecordedByProbe
        ? withoutTapAttributionParams(request.url).href
        : request.url.href);

    try {
        // `MAF-3` — the route-transition seam. The three `runNavigationViewTransition(...)` wrappers
        // below are the whole of it, and WHERE they are is the design:
        //
        //  - inside this function, so the seam covers exactly the routes the policy already sends
        //    down the soft path. A hard navigation tears the document down and has nothing to bridge,
        //    which is why the `SAFE_ROUTE_PATTERNS` work is strictly upstream of this (Q4, option A
        //    before option C);
        //  - around the SYNCHRONOUS swap only, never around an `await`. `startViewTransition` freezes
        //    the page until its callback has updated the DOM, so wrapping the release probe or the
        //    document fetch would freeze it for a network round-trip and feel slower than the blink
        //    it replaces;
        //  - re-throwing whatever the swap threw, so the catch ladder below keeps seeing `AbortError`,
        //    `FrontendReleaseHardReloadRequiredError` and `CrossLocaleNavigationUnsupportedError`.
        //
        // In an engine without the API — Firefox, older Safari, older Android WebViews, and jsdom, so
        // every unit test in this package — the wrapper runs the swap synchronously at call time and
        // this reads exactly as it did before. Guard: `PageViewTransition.spec.ts` (both halves).
        const clientOwnedRouteMatch = resolveClientRoute(request.url);
        if (clientOwnedRouteMatch) {
            const shouldMountBeforeReleaseProbe = clientOwnedRouteMatch.mountBeforeReleaseProbe === true;
            let clientOwnedRouteUrl: URL | null = null;

            if (shouldMountBeforeReleaseProbe) {
                clientOwnedRouteUrl = await runNavigationViewTransition(
                    () => mountClientOwnedRoute(clientOwnedRouteMatch, request, navigationSequence),
                );
                mountedClientRouteUrl = clientOwnedRouteUrl;
                if (request.historyMode !== "pop" && !shouldScrollToTopImmediately) {
                    window.scrollTo({ left: 0, top: 0, behavior: "auto" });
                }
            }

            const releaseProbeTargetUrl = withTapAttributionParams(
                clientOwnedRouteMatch.releaseProbeUrl ?? clientOwnedRouteMatch.url,
                request.url,
            );
            const releaseProbe = await probeFrontendRelease(releaseProbeTargetUrl, abortController.signal);
            tapAttributionRecordedByProbe = true;
            if (navigationSequence !== activeNavigationSequence) {
                endNavigation();
                return false;
            }

            ensureProbedReleaseMatchesCurrent(releaseProbe, releaseProbeTargetUrl);

            if (!clientOwnedRouteUrl) {
                clientOwnedRouteUrl = await runNavigationViewTransition(
                    () => mountClientOwnedRoute(clientOwnedRouteMatch, request, navigationSequence),
                );
                mountedClientRouteUrl = clientOwnedRouteUrl;
                if (request.historyMode !== "pop" && !shouldScrollToTopImmediately) {
                    window.scrollTo({ left: 0, top: 0, behavior: "auto" });
                }
            }

            // The mount above became a YIELD point when the view-transition seam landed: in an engine
            // that has the API the swap happens a frame later, so a second tap can begin its own
            // navigation between the mount and the completion below. Same guard, same idiom and same
            // trade as the one after `probeFrontendRelease` — a superseded navigation stops here and
            // lets the live one own the post-mount effects and the progress bar, rather than both
            // completing over the same `<main>`.
            if (navigationSequence !== activeNavigationSequence) {
                endNavigation();
                return false;
            }

            endNavigation();
            applyPostMountDocumentEffectsFromDocument(document, {
                url: clientOwnedRouteUrl,
            });
            clearNavigationTransitionSkeleton(navigationSequence);
            clearNavigationLoadFailure();
            completeNavigationProgress();
            // FI-2811: the mount replaced the content that held the clicked link. Not on a pop (it restores scroll).
            if (request.historyMode !== "pop") {
                moveFocusToNavigatedPage();
            }
            return true;
        }

        const fetchedDocument = await fetchHtmlDocument(request.url, abortController.signal);
        // FI-1936: the document fetch requested the FULL tapped address, so the server has now seen its `pl`/`ref`/
        // `nsrc` exactly as it would have through the probe. The release-mismatch hard load below must not repeat them.
        tapAttributionRecordedByProbe = true;
        if (navigationSequence !== activeNavigationSequence) {
            endNavigation();
            return false;
        }

        ensureDocumentReleaseMatchesCurrent(fetchedDocument.document);

        // FI-3330: the page a skeleton held is unmounted here, before the retire and the history write, where the
        // skeleton used to unmount it (its flushed effects still belong to its own entry and instance).
        discardHeldNavigationPage();
        // The client-route branch's ownership hand-off, on the document path (ownership design §14).
        retirePageInstance();
        if (request.historyMode === "push") {
            pushPageHistoryEntry(withRequestedFragment(fetchedDocument.url, request.url));
            if (!shouldScrollToTopImmediately) {
                window.scrollTo({ left: 0, top: 0, behavior: "auto" });
            }
        } else if (request.historyMode === "replace") {
            replacePageHistoryEntry(withRequestedFragment(fetchedDocument.url, request.url));
            if (!shouldScrollToTopImmediately) {
                window.scrollTo({ left: 0, top: 0, behavior: "auto" });
            }
        } else {
            // Pop — see the matching note in `mountClientOwnedRoute`. The id comes from the request, not
            // from `window.history.state`, because the `await probeFrontendRelease`/`await
            // fetchHtmlDocument` above are yields and the current entry can move across them.
            replacePageHistoryEntry(
                fetchedDocument.url.href,
                request.restoreScrollX ?? 0,
                request.restoreScrollY ?? 0,
                request.restoreNavigationId,
            );
        }

        await runNavigationViewTransition(() => swapDocumentContent(fetchedDocument.document));

        // The same yield the client-route branch above guards, on the document path.
        if (navigationSequence !== activeNavigationSequence) {
            endNavigation();
            return false;
        }

        if (request.historyMode === "pop") {
            // Must run *after* the swap: scrolling first would target the outgoing DOM, and the
            // incoming content would land at the top with no re-scroll. The id lets a list restored
            // from `NavigationListStateCache` finish this restore from its commit (SV-24 slice 4).
            restoreScrollPosition(
                request.restoreScrollX ?? 0,
                request.restoreScrollY ?? 0,
                request.restoreNavigationId,
            );
        } else {
            // FI-4071: a push/replace whose REQUESTED url names an element in the swapped document ends on it, not at
            // the top scrolled to above. After the swap, so the id resolves against the incoming DOM; the response URL
            // never carries the fragment, hence `request.url`. The fetched document is passed because the swap's island
            // mount can discard the SSR node before this runs. No fragment or no such element: the top scroll stands.
            restoreSoftNavigationFragmentScroll(request.url, fetchedDocument.document);
        }

        endNavigation();
        applyPostMountDocumentEffectsFromDocument(fetchedDocument.document, {
            url: fetchedDocument.url,
        });
        clearNavigationTransitionSkeleton(navigationSequence);
        clearNavigationLoadFailure();
        completeNavigationProgress();
        // FI-2811: the swap replaced the content that held the clicked link. Not on a pop (it restores scroll).
        if (request.historyMode !== "pop") {
            moveFocusToNavigatedPage();
        }
        return true;
    } catch (error) {
        if (navigationSequence !== activeNavigationSequence) {
            endNavigation();
            return false;
        }

        // FI-2814 (work order round 116 D15): the deadline, or a failure the NETWORK caused, never full-loads (offline
        // that swaps the page for the browser's error page). A client route already on screen is simply settled; any
        // other page gets the inline retry block. Checked before the hard-load branches below for that reason.
        const isAbort = (error as DOMException | undefined)?.name === "AbortError";
        const loadFailureReason: NavigationLoadFailureReason | null = settleDeadlinePassed
            ? "timeout"
            : (isAbort ? null : classifyNetworkFailure(error));
        if (loadFailureReason) {
            endNavigation();
            if (mountedClientRouteUrl) {
                applyPostMountDocumentEffectsFromDocument(document, { url: mountedClientRouteUrl });
                clearNavigationTransitionSkeleton(navigationSequence);
                clearNavigationLoadFailure();
                completeNavigationProgress();
                if (request.historyMode !== "pop") {
                    moveFocusToNavigatedPage();
                }
                return true;
            }
            endNavigationInLoadFailure(request, loadFailureReason, navigationSequence, fallbackHref(), scrollBeforeNavigation);
            return false;
        }

        // FI-4824: every exit below fails this navigation, so a skeleton its mount carried goes too. The settled exit
        // above keeps it: that route is on screen and its island is still coming (design §8.3).
        removeNavigationSkeletonCarry();

        if (isAbort) {
            clearNavigationTransitionSkeleton(navigationSequence);
            // FI-3430 (r137): this exit used to leave the held page in limbo (neither back nor unmounted) and the
            // skeleton markup on screen. No new page landed unless a client route is already mounted (its mount
            // discarded the held page), so the page being read comes back, under the same pop rule as a failure.
            if (mountedClientRouteUrl) {
                discardHeldNavigationPage();
            } else {
                putBackOrDiscardHeldPage(request, scrollBeforeNavigation);
            }
            failNavigationProgress();
            endNavigation();
            return false;
        }

        // FI-3430 (r137): every hard-load exit below leaves the document, so the held page is unmounted first (what
        // the skeleton did before FI-3330), never kept mounted with its listeners and polling until the unload.
        discardHeldNavigationPage();

        if (error instanceof FrontendReleaseHardReloadRequiredError) {
            logger.info(`[EnhancedNavigation] ${error.message}`);
            clearNavigationTransitionSkeleton(navigationSequence);
            failNavigationProgress();
            endNavigation();
            performHardFallback(fallbackHref(), request.fallbackMode);
            return false;
        }

        if (error instanceof CrossLocaleNavigationUnsupportedError) {
            // Expected, designed-for outcome: a genuine language change cannot swap the already-executed
            // messages bundle, so it hard-navigates. Logging it at `warn` made it the loudest cluster in
            // prod (63 lines / 12h on 2026-07-26, 20% of all leveled lines) and buried the real swap
            // failures below. The decision to fall back is made HERE, so the level belongs here — see
            // guides/prod-log-triage-2026-06-28.md.
            logger.info(`[EnhancedNavigation] ${error.message}`);
            clearNavigationTransitionSkeleton(navigationSequence);
            failNavigationProgress();
            endNavigation();
            performHardFallback(fallbackHref(), request.fallbackMode);
            return false;
        }

        logger.warn("Enhanced navigation failed; falling back to hard navigation.", error);
        clearNavigationTransitionSkeleton(navigationSequence);
        failNavigationProgress();
        endNavigation();
        performHardFallback(fallbackHref(), request.fallbackMode);
        return false;
    } finally {
        window.clearTimeout(settleDeadlineId);
        endNavigation();
        if (activeNavigationAbortController === abortController) {
            activeNavigationAbortController = null;
        }
    }
}

/**
 * FI-2814: end a stalled or offline navigation in the Retry banner. The progress bar ends, the marker is logged
 * (`NAV_TIMEOUT`, forwarded to `/api/client-logs`; read owed 2026-10-29), and Retry re-runs the SAME request, so a
 * failed push still pushes once and a failed pop still restores its entry. No history is written here (rule 33): a
 * push that failed never pushed. FI-3330 (r136 A5): the page being read is kept: a skeleton's held page is put back
 * at the offset the member left, and the banner sits over it (`NavigationLoadFailure`). With no body to render into,
 * an online failure keeps the old full-load fallback and an offline one stays where it is.
 */
/**
 * FI-3330 / FI-3430: the one decision for a navigation that ends without landing (a failure or an abort). r137 landing
 * review: a POP has already moved the address to the destination entry, so the outgoing page must not come back under
 * it (its in-page history writers would run over the wrong entry). Only a push or replace, whose address never moved,
 * gets its page back, at the offset the member left; a pop discards it, the pre-FI-3330 behaviour. A superseded
 * navigation calls neither: the navigation that superseded it owns the held page and settles it on its own exit.
 */
function putBackOrDiscardHeldPage(
    request: EnhancedNavigationRequest,
    scrollBeforeNavigation: { x: number; y: number },
): void {
    if (request.historyMode === "pop") {
        discardHeldNavigationPage();
    } else if (restoreHeldNavigationPage()) {
        window.scrollTo({ left: scrollBeforeNavigation.x, top: scrollBeforeNavigation.y, behavior: "auto" });
    }
}

function endNavigationInLoadFailure(
    request: EnhancedNavigationRequest,
    reason: NavigationLoadFailureReason,
    navigationSequence: number,
    fallbackHref: string,
    scrollBeforeNavigation: { x: number; y: number },
): void {
    clearNavigationTransitionSkeleton(navigationSequence);
    putBackOrDiscardHeldPage(request, scrollBeforeNavigation);
    failNavigationProgress();
    logger.warn(`NAV_TIMEOUT reason=${reason}`, {
        path: request.url.pathname,
        historyMode: request.historyMode,
        source: request.source,
    });
    const rendered = showNavigationLoadFailure(() => {
        // FI-2834: the denominator of the retry success share. Without it a failed retry logged only another
        // NAV_TIMEOUT, indistinguishable from a banner nobody retried, so recovered / NAV_TIMEOUT answered nothing.
        // Same sender and family as `recovered` below: success share = recovered / attempted.
        logger.warn(`NAV_TIMEOUT_RETRY attempted reason=${reason}`, { path: request.url.pathname });
        void runEnhancedNavigation(request, { fromLoadFailureRetry: true }).then((settled) => {
            if (settled) {
                logger.warn(`NAV_TIMEOUT_RETRY recovered reason=${reason}`, { path: request.url.pathname });
            }
        });
    });
    if (!rendered && reason !== "offline") {
        performHardFallback(fallbackHref, request.fallbackMode);
    }
}

/** FI-5545: the hard anchor decisions whose default action unloads this document in the same tab. */
const UNLOADING_HARD_REASONS = new Set<NavigationPolicyDecision["reason"]>([
    "ineligible-path",
    "explicit-hard-marker",
    "locale-change",
    "external-rel",
]);

/** FI-5575: the hard INTENT decisions that unload this document (cross-origin, protocol and fragment ones do not). */
const UNLOADING_HARD_INTENT_REASONS = new Set<NavigationPolicyDecision["reason"]>([
    "explicit-hard-policy",
    "locale-change",
    "ineligible-path",
]);

function handleDocumentClick(event: MouseEvent): void {
    const anchor = findClosestAnchor(event.target);
    if (!anchor) {
        return;
    }

    // FI-1995 (GHSA-rvm3-566m-v7fv): a link into the Capacitor shell's reserved paths is never followed,
    // whatever the modifier, target or soft/hard decision: on an app build < 7.6.9 the native layer would
    // fetch its `u=` URL and serve it at our origin. Renderers already drop such hrefs; this is the net for
    // any anchor they did not see (stored HTML from before the guard reaching a path that skips the backend).
    if (isCapacitorReservedHref(anchor.getAttribute("href")) || isCapacitorReservedHref(anchor.href)) {
        event.preventDefault();
        return;
    }

    const policyDecision = getAnchorNavigationPolicy(anchor, event, window.location);

    // FI-606: the link for the page the member is already on scrolls to the top and does nothing else:
    // no leave guard or departure observer (nobody is leaving), no history write (the address does not
    // change, so rule 33 owes no entry). `hash-only` is admitted for the one case it covers here, a live
    // `#fragment` on an otherwise identical address; every modifier/target/download case was refused
    // before either reason can be reached.
    if ((policyDecision.mode === "soft" || policyDecision.reason === "hash-only")
        && isCurrentPageReselect(anchor, window.location)) {
        event.preventDefault();
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
        return;
    }

    if (policyDecision.mode !== "soft") {
        // Inside the native Android shell, a hard navigation to an off-host destination LEAVES the app
        // with no way back. `MobileOutboundLinkBridge` re-reads the decision already in hand and hands
        // such destinations to a Chrome Custom Tab, which stays in the app's task. It is inert outside
        // the shell and it decides its capability BEFORE returning true, so a `false` here is never
        // preceded by a side effect and this line can only ever add behaviour, never remove it.
        // Design: designs/01-now/2026-07-30-native-shell-outbound-links-downloads-and-open-in-design.md.
        //
        // The parse is guarded because this branch now runs for EVERY hard navigation in the app,
        // including the ones whose href is empty or malformed. An exception thrown here would escape
        // into the document click listener and break ordinary navigation outright, which would be a far
        // worse defect than the one this call fixes.
        let outboundUrl: URL | null = null;
        try {
            outboundUrl = new URL(anchor.href, window.location.href);
        } catch {
            outboundUrl = null;
        }

        if (outboundUrl && handleOutboundAnchorNavigation(outboundUrl, policyDecision.reason)) {
            event.preventDefault();
            return;
        }

        // FI-5545 (CREATE-PUBLISH-112): a same-origin link that UNLOADS this document asks the leave guards first, so a
        // dirty form gets the in-app confirm rather than the WebView's `beforeunload` dialog. Cross-origin links are left
        // alone (the shell's bridge above took them; a browser keeps its own dialog). No claim: the click is untouched.
        if (outboundUrl
            && outboundUrl.origin === window.location.origin
            && UNLOADING_HARD_REASONS.has(policyDecision.reason)
            && runNavigationLeaveGuards({ url: outboundUrl, trigger: "anchor", hard: true })) {
            event.preventDefault();
        }
        return;
    }

    event.preventDefault();
    const url = new URL(anchor.href, window.location.href);

    // CREATE-PUBLISH-9. The cancellation point a page needs in order to protect in-progress work from
    // a FORWARD navigation. Until this existed, a page could veto Back (a popstate guard) and an
    // unload (`beforeunload`) and had no way at all to see a chrome click, because this branch turns
    // it into a soft push before anyone is asked. `preventDefault` has already run, so a claiming
    // guard leaves the member exactly where they were.
    if (runNavigationLeaveGuards({ url, trigger: "anchor" })) {
        return;
    }

    // The navigation is now certain, so tell anything that needs to react to a DEPARTURE rather than
    // object to one. Strictly after the guard above: a claimed navigation never happens, and an overlay
    // that dismissed itself for it would be closing over a page the member never left.
    runNavigationDepartureObservers({ url, trigger: "anchor" });

    void runEnhancedNavigation({
        url,
        historyMode: "push",
        trigger: "anchor",
        source: "anchor-click",
        fallbackMode: "assign",
        // FI-1868: a group link that knows its group has a banner says so; the skeleton draws no band otherwise.
        skeletonHints: { groupHasBanner: anchor.dataset.groupHasBanner === "true", link: true },
    });
}

function handleNavigationIntent(event: Event): void {
    const customEvent = event as CustomEvent<BrowserNavigationEventDetail>;
    const policyDecision = getIntentNavigationPolicy(customEvent.detail, window.location);
    if (policyDecision.mode !== "soft") {
        // FI-5575: the programmatic twin of the hard-anchor ask above. A same-origin destination that unloads this
        // document asks the leave guards; a claim cancels the intent, so `navigateToUrl` loads nothing. A guard's own
        // authorized re-issue is declined by that guard (`departureAuthorized`, `suppressBeforeUnloadRef`): one confirm.
        const hardUrl = new URL(customEvent.detail.url, window.location.href);
        if (hardUrl.origin === window.location.origin
            && UNLOADING_HARD_INTENT_REASONS.has(policyDecision.reason)
            && runNavigationLeaveGuards({ url: hardUrl, trigger: "intent", hard: true })) {
            event.preventDefault();
        }
        return;
    }

    const nextUrl = new URL(customEvent.detail.url, window.location.href);

    // The programmatic twin of the anchor branch above (CREATE-PUBLISH-9). A page's own deliberate
    // departures opt out by disarming their guard first — the composer's
    // `navigateWithoutUnsavedChangesPrompt` sets its suppression flag before it navigates — so this
    // catches the intents a page did NOT author, not the ones it did.
    if (runNavigationLeaveGuards({ url: nextUrl, trigger: "intent" })) {
        event.preventDefault();
        return;
    }

    event.preventDefault();

    // Same point in the sequence as the anchor branch above, and it must stay that way: a departure is
    // a departure whether the member clicked a link or the app navigated on their behalf, and an
    // overlay that only heard about one of the two would be correct exactly half the time.
    runNavigationDepartureObservers({ url: nextUrl, trigger: "intent" });

    void runEnhancedNavigation({
        url: nextUrl,
        historyMode: customEvent.detail.mode === "replace" ? "replace" : "push",
        trigger: "intent",
        source: customEvent.detail.source,
        fallbackMode: customEvent.detail.mode,
    });
}

/**
 * The three-way order below is a tested contract (see the truth table in EnhancedNavigation.spec.ts),
 * not an accident of line order:
 *
 *  1. Explicit page GUARDS win outright — a page holding an unsaved draft must be able to veto even its
 *     own entry.
 *  2. OWNERSHIP beats URL policy — the instance that wrote the entry knows more about it than a regex
 *     over the pathname does, and it can render the destination from live state with nothing torn down.
 *  3. Otherwise the router navigates, exactly as before.
 */
function handlePopState(event: PopStateEvent): void {
    // A page may claim this Back/Forward before the router acts (e.g. the create-discussion editor holds
    // the user on an unsaved draft and confirms). See PopStateGuardRegistry.
    if (runPopStateGuards(event)) {
        recordPopDispatch("guard");
        return;
    }

    // The page that wrote this entry is still mounted, so it can render the destination itself. Nothing
    // is unmounted, no document is fetched, and no DOM is replaced — which is the whole point: a full
    // remount produces a correct-LOOKING page while discarding scroll, loaded pagination, expanded
    // panels and any half-finished work the island was holding.
    //
    // Entries written before this shipped carry no `entryKind`, so a user mid-session across a release
    // falls through to a real navigation rather than into an undefined branch.
    const poppedState = event.state;
    if (isEnhancedNavigationHistoryState(poppedState)
        && poppedState.entryKind === "in-page"
        && poppedState.pageInstanceId === getPageInstanceId()) {
        recordPopDispatch("in-page");
        notifyInPageHistoryChange(poppedState);
        restoreScrollPosition(poppedState.scrollX ?? 0, poppedState.scrollY ?? 0, poppedState.navigationId);
        return;
    }

    const nextUrl = new URL(window.location.href);
    const policyDecision = getPopStateNavigationPolicy(nextUrl, window.location);
    if (policyDecision.mode !== "soft") {
        recordPopDispatch("hard-fallback");
        performHardFallback(nextUrl.href, "replace");
        return;
    }

    recordPopDispatch("document");
    const historyState = isEnhancedNavigationHistoryState(event.state) ? event.state : undefined;
    void runEnhancedNavigation({
        url: nextUrl,
        historyMode: "pop",
        trigger: "popstate",
        source: "popstate",
        fallbackMode: "replace",
        restoreScrollX: historyState?.scrollX ?? 0,
        restoreScrollY: historyState?.scrollY ?? 0,
        // Read HERE, off the state the browser handed us, and not again later: `runEnhancedNavigation`
        // awaits a release probe (and possibly a document fetch) before it rewrites the entry, so
        // `window.history.state` at write time is not guaranteed to still be this entry.
        restoreNavigationId: historyState?.navigationId,
    });
}

export function setupEnhancedNavigation(): void {
    if (enhancedNavigationSetupComplete || typeof window === "undefined" || typeof document === "undefined") {
        return;
    }

    // We restore scroll ourselves (restoreScrollPosition), across frames, once the incoming content is
    // tall enough to hold the offset. Leaving the browser's own "auto" restoration on means a second
    // uncoordinated writer racing ours on every pop.
    if ("scrollRestoration" in history) {
        history.scrollRestoration = "manual";
    }

    ensureInitialHistoryState();
    installPopDispatchTelemetry();
    document.addEventListener("click", handleDocumentClick);
    window.addEventListener(BROWSER_NAVIGATION_EVENT_NAME, handleNavigationIntent as EventListener);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pagehide", handlePageHide);
    // FI-3157 (r158): warm the skeleton chunk once startup work is done, so the first navigation draws it synchronously.
    // A fetch only: nothing on screen changes (rule 26).
    schedulePageNavigationTransitionIdlePreload();
    enhancedNavigationSetupComplete = true;
}

export function resetEnhancedNavigationForTests(): void {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
        document.removeEventListener("click", handleDocumentClick);
        window.removeEventListener(BROWSER_NAVIGATION_EVENT_NAME, handleNavigationIntent as EventListener);
        window.removeEventListener("popstate", handlePopState);
        window.removeEventListener("pagehide", handlePageHide);
    }

    resetPopDispatchTelemetryForTests();
    activeNavigationAbortController?.abort();
    activeNavigationAbortController = null;
    activeNavigationSequence = 0;
    hardFallbackPending = false;
    removeNavigationSkeletonCarry();
    navigationSettleTimeoutMs = NAVIGATION_SETTLE_TIMEOUT_MS;
    enhancedNavigationSetupComplete = false;
    clearNavigationLoadFailure();
    discardHeldNavigationPage();
}
